import { useEffect, useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import SEOSchema from '../../components/SEOSchema';

export default function PalletRemovalPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleQuoteClick = () => {
    const contactSection = document.getElementById('removal-form');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Convert FormData to URLSearchParams for application/x-www-form-urlencoded
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d5a6uftcrgmf5papd830', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOSchema type="service" />
      <Navigation isScrolled={isScrolled} />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 w-full h-full">
            <img
              src="https://public.readdy.ai/ai/img_res/61d87c5415a8a8c977ca8ca6d675ccdf.jpg"
              alt="Pallet Removal Nationwide"
              title="Professional Pallet Removal Services"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
          </div>
          
          <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white">
            <h1 className="text-6xl lg:text-7xl font-bold mb-6">
              Pallet Removal Nationwide
            </h1>
            <p className="text-xl lg:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
              Fast, reliable, and eco-friendly pallet removal services across all 50 states. We handle everything from pickup to recycling, helping you reclaim valuable warehouse space.
            </p>
            <button
              onClick={handleQuoteClick}
              className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
            >
              Schedule Pickup
            </button>
          </div>
        </section>

        {/* Service Overview */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="w-full h-[500px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Large%20stack%20of%20used%20wooden%20pallets%20ready%20for%20removal%20and%20recycling%2C%20organized%20warehouse%20setting%2C%20industrial%20environment%2C%20clean%20simple%20background%2C%20professional%20photography%20with%20good%20lighting&width=600&height=500&seq=removalstacknew&orientation=portrait"
                  alt="Pallet Removal Service"
                  title="Used Pallet Collection"
                  className="w-full h-full object-cover object-center rounded-2xl shadow-2xl"
                />
              </div>

              <div>
                <p className="text-orange-600 text-sm font-bold mb-3 flex items-center">
                  <span className="w-2 h-2 bg-orange-600 rounded-full mr-2"></span>
                  PROFESSIONAL SERVICE
                </p>
                <h2 className="text-5xl font-bold text-gray-900 mb-6">
                  Why Choose Our Pallet Removal Service?
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  When you need <strong>Pallet Removal Nationwide</strong>, we provide a hassle-free solution that saves you time and money. Our experienced team handles the entire process, from scheduling to final disposal or recycling.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Whether you have a few dozen pallets or thousands, we have the equipment and expertise to remove them efficiently. Plus, we prioritize recycling and reuse to minimize environmental impact.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Simple, straightforward process from start to finish
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-24 h-24 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl font-bold text-teal-600">1</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Request Quote</h3>
                <p className="text-gray-600 leading-relaxed">
                  Fill out our simple form with details about your pallet removal needs
                </p>
              </div>

              <div className="text-center">
                <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl font-bold text-orange-600">2</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Get Estimate</h3>
                <p className="text-gray-600 leading-relaxed">
                  Receive a competitive quote within 24 hours based on your requirements
                </p>
              </div>

              <div className="text-center">
                <div className="w-24 h-24 bg-lime-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl font-bold text-lime-600">3</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Schedule Pickup</h3>
                <p className="text-gray-600 leading-relaxed">
                  Choose a convenient time for our team to collect your pallets
                </p>
              </div>

              <div className="text-center">
                <div className="w-24 h-24 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl font-bold text-teal-600">4</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">We Handle Rest</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our team removes pallets and ensures proper recycling or disposal
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Offered */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">
                Our Removal Services
              </h2>
              <p className="text-xl text-gray-600">
                Comprehensive solutions for all your pallet removal needs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <article className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-delete-bin-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">One-Time Removal</h3>
                <p className="text-gray-600 leading-relaxed">
                  Perfect for warehouse cleanouts, relocations, or when you need to clear space quickly. We handle any quantity.
                </p>
              </article>

              <article className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-calendar-check-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Scheduled Pickups</h3>
                <p className="text-gray-600 leading-relaxed">
                  Regular removal service on your schedule. Weekly, bi-weekly, or monthly pickups to keep your facility organized.
                </p>
              </article>

              <article className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-lime-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-alarm-warning-line text-3xl text-lime-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Emergency Service</h3>
                <p className="text-gray-600 leading-relaxed">
                  Need immediate removal? We offer expedited service for urgent situations with same-day or next-day pickup.
                </p>
              </article>

              <article className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-recycle-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Recycling Program</h3>
                <p className="text-gray-600 leading-relaxed">
                  We prioritize eco-friendly disposal. Pallets are sorted for repair, recycling, or responsible disposal.
                </p>
              </article>

              <article className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-exchange-dollar-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Buy-Back Options</h3>
                <p className="text-gray-600 leading-relaxed">
                  Have quality used pallets? We may purchase them from you, turning your waste into revenue.
                </p>
              </article>

              <article className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-lime-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-file-list-3-line text-3xl text-lime-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Documentation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Receive detailed reports and certificates for compliance, sustainability tracking, and record-keeping.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 bg-gradient-to-br from-teal-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">
                Why Choose Us?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                The benefits of partnering with us for <strong>Pallet Removal Nationwide</strong>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Nationwide Coverage</h3>
                    <p className="text-gray-600 leading-relaxed">
                      We serve all 50 states with a network of trusted partners, ensuring consistent service quality no matter where you're located.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                    <i className="ri-price-tag-3-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Competitive Pricing</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Transparent pricing with no hidden fees. Volume discounts available for large quantities and recurring service contracts.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-lime-100 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                    <i className="ri-time-line text-2xl text-lime-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Fast Response Time</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Quick turnaround from quote to pickup. Most requests are fulfilled within 48-72 hours, with emergency service available.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                    <i className="ri-leaf-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Eco-Friendly Practices</h3>
                    <p className="text-gray-600 leading-relaxed">
                      We maximize recycling and reuse, diverting pallets from landfills and supporting your sustainability initiatives.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Fully Insured</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Complete liability coverage protects your facility during removal operations. Safety is our top priority.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-lime-100 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                    <i className="ri-customer-service-2-line text-2xl text-lime-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Dedicated Support</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Our team is available to answer questions, provide updates, and ensure your complete satisfaction with our service.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Industries Served */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">
                Industries We Serve
              </h2>
              <p className="text-xl text-gray-600">
                Trusted by businesses across multiple sectors
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-store-2-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Retail</h3>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-building-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Warehousing</h3>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-lime-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-factory-line text-3xl text-lime-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Manufacturing</h3>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-truck-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Distribution</h3>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-restaurant-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Food Service</h3>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-lime-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-hospital-line text-3xl text-lime-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Healthcare</h3>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-hammer-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Construction</h3>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-car-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Automotive</h3>
              </div>
            </div>
          </div>
        </section>

        {/* Request Form Section */}
        <section id="removal-form" className="py-24 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">
                Schedule Your Pallet Removal
              </h2>
              <p className="text-xl text-gray-600">
                Fill out the form below and we'll contact you within 24 hours
              </p>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="pallet-removal-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Your Company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="quantity" className="block text-sm font-semibold text-gray-700 mb-2">
                      Approximate Quantity *
                    </label>
                    <select
                      id="quantity"
                      name="quantity"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Select Quantity</option>
                      <option value="1-50">1-50 pallets</option>
                      <option value="51-100">51-100 pallets</option>
                      <option value="101-500">101-500 pallets</option>
                      <option value="501-1000">501-1,000 pallets</option>
                      <option value="1000+">1,000+ pallets</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="service-type" className="block text-sm font-semibold text-gray-700 mb-2">
                      Service Type *
                    </label>
                    <select
                      id="service-type"
                      name="service-type"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Select Service</option>
                      <option value="one-time">One-Time Removal</option>
                      <option value="scheduled">Scheduled Pickups</option>
                      <option value="emergency">Emergency Service</option>
                      <option value="buyback">Buy-Back Option</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                    Pickup Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Street Address, City, State, ZIP"
                  />
                </div>

                <div>
                  <label htmlFor="preferred-date" className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred Pickup Date
                  </label>
                  <input
                    type="date"
                    id="preferred-date"
                    name="preferred-date"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                  />
                </div>

                <div>
                  <label htmlFor="pallet-condition" className="block text-sm font-semibold text-gray-700 mb-2">
                    Pallet Condition
                  </label>
                  <select
                    id="pallet-condition"
                    name="pallet-condition"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    <option value="">Select Condition</option>
                    <option value="good">Good - Reusable</option>
                    <option value="fair">Fair - Some Damage</option>
                    <option value="poor">Poor - Broken/Damaged</option>
                    <option value="mixed">Mixed Condition</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Additional Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about access requirements, loading dock availability, or any special instructions..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                {formStatus === 'success' && (
                  <div className="bg-teal-50 border border-teal-200 text-teal-800 px-4 py-3 rounded-lg">
                    <p className="font-semibold">Thank you! Your request has been submitted successfully.</p>
                    <p className="text-sm">We'll contact you within 24 hours.</p>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    <p className="font-semibold">Oops! Something went wrong.</p>
                    <p className="text-sm">Please try again or contact us directly.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}